//#region Util Imports

import * as $ from 'jquery'

//#endregion

import { IOpData, IReferenceType, IOpChangeState, IOpObject, ICopyable } from '../util/interfaces'
import { Enums } from '../common/enums'
import { DataProvider } from '../../helpers/data-provider'
import { Dictionary } from '../../helpers/dictionary'
import { CommonHelper } from '../../helpers/common-helper'
import { ObjectCustomString } from '../../models/util/object-custom-string'

import { OpDataList } from '../util/op-data-list'
import { OpDataType } from './op-data-type'
import { OpDescr } from './op-descr'
import { OpDistributorData } from './op-distributor-data'
import { OpSupplier } from './op-supplier'
import { Utils } from '../util/utils'

export class OpDistributor implements IReferenceType, IOpObject, IOpChangeState, ICopyable<OpDistributor> {

    // #region Properties

    public idDistributor: number
    public name: string
    public city: string
    public address: string
    public idDescr: number

    // #endregion


    // #region Navigation Properties

    private _descr: OpDescr = null
    public get descr(): OpDescr {
        return this._descr
    }
    public set descr(value: OpDescr) {
        this._descr = value
        this.idDescr = value != null ? value.idDescr : null
    }

    // #endregion


    //#region Custom Properties

    public childDistributors: OpDistributor[]

    public currentSuppliers: OpSupplier[]

    //#endregion


    // #region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpDistributor) {
            this.copy(<OpDistributor>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    // #endregion


    // #region IOpChangeState

    public opState: Enums.OpChangeState

    // #endregion


    // #region IReferenceType

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdDistributor
    }

    public getReferenceKey(): number {
        return this.idDistributor
    }

    public getReferenceValue(): any {
        return this
    }

    // #endregion


    // #region IOpObject

    public get idObject(): any {
        return this.idDistributor
    }

    public set idObject(idObject: any) {
        this.idDistributor = idObject
    }

    public dataList: OpDataList<OpDistributorData> = new OpDataList<OpDistributorData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj != null) {
            if (compressed || Utils.isJSONCompressed(opObj)) {
                this.descr = opObj.D != null ? new OpDescr(opObj.D) : null

                if (opObj.DL != null) {
                    this.dataList.push(...opObj.DL.map(data => new OpDistributorData(data)))
                }

                if (opObj.DV != null) {
                    this.dynamicValues = opObj.DV
                }

                if (opObj.CS != null) {
                    this.currentSuppliers = opObj.CS.map((supplier: any) => new OpSupplier(supplier))
                }

                this.childDistributors = opObj.CD != null ? opObj.CD.map((childObj: any) => new OpDistributor(childObj)) : null

                this.idDistributor = opObj.I
                this.name = opObj.N
                this.city = opObj.C
                this.address = opObj.A
                this.idDescr = opObj.ID
                this.opState = opObj.O

            } else {
                this.descr = opObj._Descr != null ? new OpDescr(opObj._Descr) : null
                this.childDistributors = opObj.ChildDistributors != null ? opObj.ChildDistributors.map((childObj: any) => new OpDistributor(childObj)) : null

                if (opObj.DataList) {
                    this.dataList.push(...opObj.DataList.map(data => new OpDistributorData(data)))
                }

                if (opObj.CurrentSuppliers != null) {
                    this.currentSuppliers = opObj.CurrentSuppliers.map((supplier: any) => new OpSupplier(supplier))
                }

                this.idDistributor = opObj.ID_DISTRIBUTOR
                this.name = opObj.NAME
                this.city = opObj.CITY
                this.address = opObj.ADDRESS
                this.idDescr = opObj.ID_DESCR
                this.dynamicValues = opObj.dynamicValues
                this.opState = opObj.OpState
            }
        }

    }

    public toJSON(compressed: boolean = false): any {
        if (compressed) {
            const result = {
                I: this.idDistributor,
                N: this.name,
                C: this.city,
                A: this.address,
                ID: this.idDescr,
                DL: this.dataList.ToJSON(),
                O: this.opState,
                CD: this.childDistributors != null ? this.childDistributors.map((childDistributor: OpDistributor) => childDistributor.toJSON(compressed)) : null,
                DV: this.dynamicValues,
                CS: this.currentSuppliers != null ? this.currentSuppliers.map((supplier: OpSupplier) => supplier.toJSON(compressed)) : null
            }

            Utils.setJSONCompressedFlag(result)

            return result
        }

        return {
            ID_DISTRIBUTOR: this.idDistributor,
            NAME: this.name,
            CITY: this.city,
            ADDRESS: this.address,
            ID_DESCR: this.idDescr,
            DataList: this.dataList.ToJSON(),
            OpState: this.opState,

            dynamicValues: this.dynamicValues,

            ChildDistributors: this.childDistributors != null ? this.childDistributors.map((childDistributor: OpDistributor) => childDistributor.toJSON(compressed)) : null,
            CurrentSuppliers: this.currentSuppliers != null ? this.currentSuppliers.map((supplier: OpSupplier) => supplier.toJSON(compressed)) : null
        }
    }

    public toString() {
        const customFormatString = ObjectCustomString.getCustomStringFormat(this)

        if (!customFormatString) {
            return this.name
        }

        return ObjectCustomString.getObjectCustomString(customFormatString, this)
    }

    // #endregion


    public static loadNavigationProperties(dataProvider: DataProvider, list: OpDistributor[]): JQueryPromise<OpDistributor[]> {
        var defered = $.Deferred<OpDistributor[]>()

        if (dataProvider && list && list.length > 0) {
            var idDataType: number[] = CommonHelper.getIdDataTypesFromObjectList<OpDistributor>(list)
            dataProvider.System.getDictionaries([Enums.ReferenceType.IdDataType]).then(dict => {
                var dataTypeDict: Dictionary<OpDataType> = new Dictionary<OpDataType>(null)
                if (dict.containsKey(Enums.ReferenceType.IdDataType)) {
                    dataTypeDict = <Dictionary<OpDataType>>dict.tryGetValue(Enums.ReferenceType.IdDataType)
                }

                list.forEach(item => {
                    if (item.dataList) {
                        item.dataList
                            .toArray()
                            .forEach((data: OpDistributorData) => {
                                data.dataType = dataTypeDict.tryGetValue(data.idDataType)
                            })
                    }
                })
                defered.resolve(list)
            })
        } else {
            defered.resolve(list)
        }
        return defered.promise()
    }

    public static fromIOpObject(object: IOpObject): OpDistributor {
        var newObject: OpDistributor = new OpDistributor(null)
        newObject.idDistributor = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpDistributorData.fromIOpData(data)))
        return newObject
    }

    // #region ICopyable

    public clone(includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean) {

        const distributor: OpDistributor = new OpDistributor(null)

        distributor.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return distributor
    }

    public copy(source: OpDistributor, includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean) {

        if (source == null) {
            return null
        }

        if (includeNavigationProperties) {
            if (source.descr) {
                // TODO
                this.descr = source.descr
            }
        }

        if (includeCustomProperties) {
            // No Custom Properties
        }

        if (includeDataList) {
            if (source.dataList != null) {
                for (let key of source.dataList.toArray()) {
                    this.dataList.push(key.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
                }
            }
        }

        if (includeDynamicValues) {
            this.dynamicValues = source.dynamicValues
        }

        this.idDistributor = source.idDistributor
        this.name = source.name
        this.city = source.city
        this.address = source.address
        this.idDescr = source.idDescr
        this.opState = source.opState
    }

    // #endregion
}